import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Building2,
  Clock,
  Smile,
  TrendingUp,
  Trophy,
  Star,
  Users,
  Shield,
  Wrench,
  CheckCircle,
  ChevronRight,
  Send
} from 'lucide-react';
import { mallBenefits, operationalExcellence, partnerLocations } from '../data/mock';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  clock: Clock,
  smile: Smile,
  building: Building2,
  trophy: Trophy,
  'trending-up': TrendingUp,
  star: Star,
};

const ForPartnersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact/partner`, formData);
      toast.success(response.data.message);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting partner enquiry:', error);
      toast.error('Failed to send enquiry. Please try again or email us directly at hello@jollytotsrides.co.za');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold">
                <span className="jolly-text-teal">A Smarter Way to</span>
                <br />
                <span className="jolly-text-orange">Serve Families.</span>
              </h1>
              <p className="text-gray-600 text-lg md:text-xl max-w-xl">
                Partner with Jolly Tots Rides to enhance customer experience, increase dwell time, and position your mall as a family-friendly destination.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#partner-form">
                  <Button className="jolly-btn-orange">
                    Partner with Us
                  </Button>
                </a>
                <Link to="/how-it-works">
                  <Button className="jolly-btn-outline">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Cover Image with Blur Background */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl relative">
                {/* Blurred background layer */}
                <div 
                  className="absolute inset-0 scale-110"
                  style={{
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/pq4gaxal_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2011_25_46%20AM.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(8px)',
                  }}
                />
                {/* Main image */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/pq4gaxal_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2011_25_46%20AM.png"
                  alt="Mother and child enjoying Jolly Tots Ride in mall"
                  className="w-full h-auto object-cover relative z-10"
                  style={{ maxHeight: '450px', objectPosition: 'center' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Benefits for Malls</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover how Jolly Tots Rides can add value to your shopping centre.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mallBenefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <Card key={benefit.id} className="jolly-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-xl bg-[hsl(45,100%,90%)] flex items-center justify-center">
                          <Icon className="w-6 h-6 jolly-text-orange" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold jolly-text-teal mb-1">{benefit.text}</h3>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operational Excellence Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="jolly-section-title">Operational Excellence</h2>
              <p className="text-gray-600 text-lg">
                We handle all operations so you can focus on what matters most — your customers.
              </p>
              <div className="space-y-4">
                {operationalExcellence.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Card className="jolly-card">
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 jolly-text-blue mx-auto mb-3" />
                  <h4 className="font-bold text-gray-800">Trained Staff</h4>
                  <p className="text-sm text-gray-600">On-site attendants</p>
                </CardContent>
              </Card>
              <Card className="jolly-card">
                <CardContent className="p-6 text-center">
                  <Shield className="w-10 h-10 jolly-text-orange mx-auto mb-3" />
                  <h4 className="font-bold text-gray-800">Risk Mitigation</h4>
                  <p className="text-sm text-gray-600">Full insurance coverage</p>
                </CardContent>
              </Card>
              <Card className="jolly-card">
                <CardContent className="p-6 text-center">
                  <Wrench className="w-10 h-10 jolly-text-teal mx-auto mb-3" />
                  <h4 className="font-bold text-gray-800">Maintenance</h4>
                  <p className="text-sm text-gray-600">Regular upkeep</p>
                </CardContent>
              </Card>
              <Card className="jolly-card">
                <CardContent className="p-6 text-center">
                  <Clock className="w-10 h-10 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-800">Check-in/out</h4>
                  <p className="text-sm text-gray-600">Structured process</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Current Partners Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Pilot Locations</h2>
            <p className="text-gray-600 text-lg">Join our growing network of mall partners.</p>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {partnerLocations.map((partner) => (
              <div key={partner.name} className="text-center">
                <div className="w-32 h-20 bg-gray-100 rounded-xl flex items-center justify-center mb-2">
                  <span className="text-xl font-bold jolly-text-blue">{partner.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Safety You Can Trust</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Clear signage & safety briefings</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Rigorous attendant training & checks</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Full liability insurance coverage</span>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Form Section */}
      <section id="partner-form" className="py-16 md:py-24 bg-[hsl(210,90%,55%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Let's Build Safer Shopping Spaces Together
              </h2>
              <p className="text-white/80 text-lg">
                Ready to enhance your mall's family experience? Get in touch with our partnership team.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(45,100%,70%)]" />
                  <span>No capital investment required</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(45,100%,70%)]" />
                  <span>Full operational support</span>
                </div>
              </div>
            </div>
            
            <Card className="shadow-2xl">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl font-bold jolly-text-teal mb-6">Partner with Us</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Mall or Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Company name"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your interest..."
                      rows={4}
                      className="rounded-xl"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="jolly-btn-orange w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Partner with Us
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Partners Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Our Partners</h2>
            <p className="text-gray-600 text-lg">Trusted partnerships that help us deliver excellence.</p>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-12">
            <a 
              href="https://www.amaconsultingservices.online/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/l9sp8dma_AMA%20Logo.jpg"
                  alt="AMA Consulting Services"
                  className="h-24 w-auto mx-auto mb-4"
                />
                <p className="text-center font-semibold text-gray-700 group-hover:jolly-text-orange transition-colors">
                  AMA Consulting Services
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForPartnersPage;
